import type { ReactNode } from 'react';
import { useState, useEffect, useMemo, forwardRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Picture, ProductCard } from '@ui';
import { oysterBarMenuImage } from '@images';
import { indexAdditionRecommendProductsQueryOptions } from '@queries/indexProducts';
import { oysterBarChefMenuQueryOptions } from '@queries/catalogProducts';
import { useKeenSlider } from 'keen-slider/react';
import ArrowCircle from '@components/Carousel/components/ArrowCircle';
import useWindowSize from '@customHooks/useWindowSize';
import './styles.scss';
import clsx from 'clsx';
import { Link } from '@compat/router';

interface AuthorMenuProps {
  type?: 'main-hits' | string;
  title?: ReactNode;
  isYellow?: boolean;
  withoutLink?: boolean;
  menuLink?: string;
}

const AuthorMenu = forwardRef<HTMLElement, AuthorMenuProps>(
  (
    {
      type,
      menuLink,
      withoutLink = false,
      title = (
        <h2 className='author-menu__title oysterbar-test-title-secondary'>
          <span className='author-menu__title-desktop'>
            <span className='author-menu__title-line'>Авторское</span>
            <span className='author-menu__title-line'>меню от нашего</span>
            <span className='author-menu__title-line'>шеф-повара</span>
          </span>
          <span className='author-menu__title-mobile'>
            <span className='author-menu__title-line'>Авторское меню от</span>
            <span className='author-menu__title-line'>нашего шеф-повара</span>
          </span>
        </h2>
      ),
      isYellow
    },
    ref
  ) => {
    const [loaded, setLoaded] = useState(false);
    const { isMobile } = useWindowSize();

    const isMainHits = type === 'main-hits';
    const hitsQuery = useQuery({
      ...indexAdditionRecommendProductsQueryOptions(6),
      enabled: isMainHits
    });
    // Общая фабрика с SSR-префетчем: список id входит в ключ, дублировать
    // его в реестре префетча нельзя — разъедется
    const listQuery = useQuery({
      ...oysterBarChefMenuQueryOptions(),
      enabled: !isMainHits
    });

    const chefProducts = useMemo(
      () =>
        ((isMainHits ? hitsQuery.data : listQuery.data) ?? []).map(
          (product: any) => ({
            ...product,
            isFavorite: false,
            isPostponed: false
          })
        ),
      [isMainHits, hitsQuery.data, listQuery.data]
    );

    const options = {
      rubberband: false,
      loop: false,
      mode: 'free-snap' as const,
      slides: {
        perView: 'auto' as const,
        spacing: 16
      },
      created() {
        setLoaded(true);
      }
    };

    const [sliderRef, instanceRef] = useKeenSlider(options);

    useEffect(() => {
      if (instanceRef?.current) {
        instanceRef.current?.update(options, 0);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chefProducts]);

    // const handleMenuClick = (): void => {
    //   const link = document.createElement('a');
    //   link.href = menuLink || 'https://www.delikateska.ru/barmenu/zhk-sobytie';
    //   // 'https://storage.delikateska.ru/c/2/ed6d201c-3cd5-4f47-9775-c5fb91c1d6aa.png';
    //   link.target = '_blank';
    //   link.click();
    // };

    return (
      <section
        ref={ref}
        id='menu'
        className={clsx('author-menu', isYellow && 'author-menu--yellow')}
      >
        <div className='author-menu__container'>
          <div className='author-menu__content'>
            {title}

            {!withoutLink && (
              <div className='author-menu__button'>
                <Link
                  to={menuLink || '/barmenu/zhk-sobytie'}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='h-[54px] flex items-center px-8 text-white transition-colors bg-[var(--dark-red)] hover:bg-[var(--main-red)] rounded-lg w-max sm:w-full allMd:h-10  allMd:text-sm justify-center'
                >
                  Смотреть меню
                </Link>
              </div>
            )}
          </div>

          <div className='author-menu__image'>
            <Picture icon={oysterBarMenuImage} />
          </div>

          <div className='author-menu__products-scroll'>
            {!isMobile && loaded && instanceRef.current && (
              <div className='arrow-circle-container'>
                <ArrowCircle
                  left
                  onClick={e => {
                    e.stopPropagation();
                    if (instanceRef.current) {
                      instanceRef.current.prev();
                    }
                  }}
                />
                <ArrowCircle
                  onClick={e => {
                    e.stopPropagation();
                    if (instanceRef.current) {
                      instanceRef.current.next();
                    }
                  }}
                  disabled={false}
                />
              </div>
            )}
            <div
              ref={sliderRef}
              className='keen-slider author-menu__products-list'
            >
              {chefProducts.map(product => (
                <div
                  key={product.id}
                  className='keen-slider__slide author-menu__product-wrapper'
                >
                  <ProductCard item={product} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }
);

export default AuthorMenu;
